// Utilitário para gerenciar rascunhos de mensagens no LocalStorage

export interface DraftMessage {
  ticketId: string;
  content: string;
  timestamp: number;
  userId?: string;
}

const DRAFT_KEY_PREFIX = 'sinticket_draft_';
const DRAFT_EXPIRY_TIME = 7 * 24 * 60 * 60 * 1000; // 7 dias em milissegundos

export class DraftManager {
  // Salvar rascunho para um ticket específico
  static saveDraft(ticketId: string, content: string, userId?: string): void {
    if (!ticketId || !content.trim()) {
      // Se não há conteúdo, remove o rascunho
      this.removeDraft(ticketId);
      return;
    }

    const draft: DraftMessage = {
      ticketId,
      content: content.trim(),
      timestamp: Date.now(),
      userId
    };

    try {
      localStorage.setItem(`${DRAFT_KEY_PREFIX}${ticketId}`, JSON.stringify(draft));
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
      // Tentar limpar rascunhos antigos se o storage estiver cheio
      this.cleanOldDrafts();
    }
  }

  // Carregar rascunho para um ticket específico
  static loadDraft(ticketId: string): string | null {
    if (!ticketId) return null;

    try {
      const stored = localStorage.getItem(`${DRAFT_KEY_PREFIX}${ticketId}`);
      if (!stored) return null;

      const draft: DraftMessage = JSON.parse(stored);
      
      // Verificar se o rascunho não expirou
      if (Date.now() - draft.timestamp > DRAFT_EXPIRY_TIME) {
        this.removeDraft(ticketId);
        return null;
      }

      return draft.content;
    } catch (error) {
      console.error('Erro ao carregar rascunho:', error);
      this.removeDraft(ticketId); // Remove rascunho corrompido
      return null;
    }
  }

  // Remover rascunho de um ticket específico
  static removeDraft(ticketId: string): void {
    if (!ticketId) return;
    
    try {
      localStorage.removeItem(`${DRAFT_KEY_PREFIX}${ticketId}`);
    } catch (error) {
      console.error('Erro ao remover rascunho:', error);
    }
  }

  // Limpar todos os rascunhos expirados
  static cleanOldDrafts(): void {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(DRAFT_KEY_PREFIX)) {
          try {
            const draft: DraftMessage = JSON.parse(localStorage.getItem(key) || '{}');
            if (now - draft.timestamp > DRAFT_EXPIRY_TIME) {
              localStorage.removeItem(key);
            }
          } catch {
            // Remove rascunhos corrompidos
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Erro ao limpar rascunhos antigos:', error);
    }
  }

  // Listar todos os rascunhos ativos (útil para debugging)
  static listAllDrafts(): DraftMessage[] {
    const drafts: DraftMessage[] = [];
    
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(DRAFT_KEY_PREFIX)) {
          try {
            const draft: DraftMessage = JSON.parse(localStorage.getItem(key) || '{}');
            if (now - draft.timestamp <= DRAFT_EXPIRY_TIME) {
              drafts.push(draft);
            }
          } catch {
            // Ignora rascunhos corrompidos
          }
        }
      });
    } catch (error) {
      console.error('Erro ao listar rascunhos:', error);
    }
    
    return drafts;
  }

  // Verificar se existe rascunho para um ticket
  static hasDraft(ticketId: string): boolean {
    return this.loadDraft(ticketId) !== null;
  }
}
